import { ISection, ISectionItem } from '@creditflowresearch/uilib/Section';
import { HeaderMappingItem } from '@/creditflowresearch/application/actions/profile/helpers/types';
import { IMenuProfile } from '@/creditflowresearch/domain/DictionaryMapping';

interface IProfileItem {
  id: string,
  link: string,
  name: string,
  active?: boolean,
  beta?: boolean,
  color?: string,
}

interface ITransformOptions {
  activeProductAbbreviation: string,
  activeRegionAbbreviation: string,
  activeMapping: HeaderMappingItem,
  filterType: string,
}

class TransformerService {
  private static hasDeals(
    activeMapping: HeaderMappingItem,
    filterType: string,
    parentId: string,
    childId?: string,
  ): boolean {
    const mapping = filterType === 'sector' ? activeMapping.sector : activeMapping.industry;
    if (!mapping) return false;

    if (parentId && !childId) {
      if (Array.isArray(mapping)) {
        return mapping.some((item) => Object.hasOwn(item, parentId));
      }
      return Object.hasOwn(mapping, parentId);
    }

    if (parentId && childId) {
      if (Array.isArray(mapping)) {
        return mapping.some((item) =>
          Object.hasOwn(item, parentId) &&
          typeof item[parentId] === 'object' &&
          item[parentId] !== null &&
          Object.hasOwn(item[parentId], childId),
        );
      }
      return Object.hasOwn(mapping, parentId) &&
        typeof mapping[parentId] === 'object' &&
        mapping[parentId] !== null &&
        Object.hasOwn(mapping[parentId], childId);
    }

    return false;
  }

  private static transformProfileToSection(
    profile: IMenuProfile,
    options: ITransformOptions,
  ): ISection {
    const { activeProductAbbreviation, activeRegionAbbreviation } = options;
    const linkQuery = {
      products: activeProductAbbreviation,
      regions: activeRegionAbbreviation,
    };

    const parentHasDeals = TransformerService.hasDeals(
      options.activeMapping,
      options.filterType,
      profile.id,
    );

    const items: ISectionItem[] = (profile.children || []).map((child: IProfileItem) => {
      const childHasDeals = TransformerService.hasDeals(
        options.activeMapping,
        options.filterType,
        profile.id,
        child.id,
      );

      return {
        label: child.name,
        href: childHasDeals
          ? { pathname: `/${ child.link }`, query: linkQuery }
          : undefined,
        disabled: !childHasDeals,
        beta: child.beta || false,
      };
    });

    return {
      id: profile.id,
      sectionLabel: profile.name,
      sectionBadge: profile.abbreviation,
      sectionColor: profile.color || '#000000',
      sectionHref: parentHasDeals
        ? { pathname: `/${ profile.link }`, query: linkQuery }
        : undefined,
      items,
    };
  }

  static transformProfilesToSections(
    profilesMenu: IMenuProfile[][][],
    options: ITransformOptions,
  ): ISection[][][] {
    if (!profilesMenu?.length) {
      return [];
    }

    return profilesMenu.map((row) =>
      Array.isArray(row)
        ? row.map((column) =>
          Array.isArray(column)
            ? column.map((profile: IMenuProfile) => TransformerService.transformProfileToSection(profile, options))
            : [],
        )
        : [],
    );
  }
}

export default TransformerService;
